import { LightweightElement } from "@/store/lightweight-elements-slice";
import { locationFilterForReportAndSnapshotTree } from "@/utils/tree-generation/custom-tree-filters";
import { ITreeRenderDecision } from "@/utils/tree-generation/tree-render-filters";
import {
  GUID,
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";

/**
 * @returns Shows tree of sheets at the top level and the locations being their children
 *    Also, display the timeseries elements of the locations
 * @param iElement IElement to use as base for the filtering
 * @param parentType Parent type of the given iElement
 * @param videoModeCopyTagId ID of label tagging video mode copies
 * @param customDate Custom date to use instead of the element's createdAt date
 */
export function reportFilter(
  iElement: IElement | LightweightElement,
  parentType?: string,
  videoModeCopyTagId?: GUID,
  customDate?: string,
): ITreeRenderDecision {
  if (iElement.type === IElementType.section) {
    const { typeHint } = iElement;

    if (typeHint === IElementTypeHint.area) {
      // Keep the parent of the floor plan pdfs folder
      return {
        shouldKeepElement: true,
        shouldKeepChildren: true,
      };
    }

    if (
      // Do not display BIM model sections in the report tree
      typeHint === IElementTypeHint.bimModel
    ) {
      return {
        shouldKeepElement: false,
        shouldKeepChildren: false,
      };
    }

    if (typeHint === IElementTypeHint.cluster) {
      // Do not show the clusters in the report tree
      return {
        shouldKeepElement: false,
        shouldKeepChildren: true,
      };
    }

    if (
      typeHint === IElementTypeHint.focusScan ||
      typeHint === IElementTypeHint.orbisScan ||
      typeHint === IElementTypeHint.elsScan ||
      typeHint === IElementTypeHint.focusPanoCalibrationScan ||
      typeHint === IElementTypeHint.focusPanoScan ||
      typeHint === IElementTypeHint.photogrammetryScan
    ) {
      // Do not show the children of the scan sections in the report tree
      return {
        shouldKeepElement: true,
        shouldKeepChildren: false,
      };
    }

    // Do not show registration scans in the report tree
    if (typeHint === IElementTypeHint.registrationScan) {
      return {
        shouldKeepElement: false,
        shouldKeepChildren: false,
      };
    }

    // Do not show the floor plan generated by customer using Floorplan Generation tool
    if (typeHint === IElementTypeHint.layer) {
      return {
        shouldKeepElement: false,
        shouldKeepChildren: false,
      };
    }
  }

  if (iElement.type === IElementType.cluster) {
    if (iElement.typeHint === IElementTypeHint.rootCluster) {
      return {
        shouldKeepElement: false,
        shouldKeepChildren: true,
      };
    }

    if (iElement.typeHint === IElementTypeHint.siteScapeCapture) {
      return {
        shouldKeepElement: false,
        shouldKeepChildren: false,
      };
    }

    // Keep the clusters
    return {
      shouldKeepElement: true,
      shouldKeepChildren: true,
    };
  }

  if (
    iElement.type === IElementType.timeSeries &&
    iElement.typeHint === IElementTypeHint.dataSession &&
    iElement.childrenIds?.length
  ) {
    return {
      shouldKeepElement: true,
      shouldKeepChildren: true,
      displayName: "4D Sessions",
    };
  }

  return locationFilterForReportAndSnapshotTree(
    iElement,
    parentType,
    videoModeCopyTagId,
    customDate,
  );
}
